import { useEffect, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import {
  accionsApi,
  BLOCS,
  useCercaServeis,
  useZones,
  type ActivacioAltaDto,
  type ServeiCercaDto,
} from '@/api/activacions';
import { ApiError } from '@/api/client';
import { useMissatgeError } from '@/api/errors';
import type { BlocDestinatari } from '@/api/types';
import { Desplegable } from '@/components/Desplegable';
import { Icona, type NomIcona } from '@/components/Icona';
import { Toolbar } from '@/layout/Toolbar';
import { useMe } from '@/permisos/MeContext';
import { Avatar, BadgeArea, Buit, Camp } from './comu';
import { isoAmbZona } from './suport';

/** Retard abans de recalcular la previsualització mentre s'escriu. */
const RETARD_PREVI_MS = 400;

function novaClau(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function iconaServei(s: ServeiCercaDto): [NomIcona, string] {
  if (s.serveiId == null) return ['building', 'bg-gray'];
  return s.tipus === 'CRISI' ? ['flame', 'bg-purple'] : ['bolt', 'bg-blue'];
}

/** Valor estabilitzat: només canvia quan `valor` (serialitzat) fa `ms` que no es mou. */
function useEstable<T>(valor: T, ms: number): T {
  const clau = JSON.stringify(valor);
  const [estable, setEstable] = useState(valor);
  useEffect(() => {
    const h = setTimeout(() => setEstable(JSON.parse(clau) as T), ms);
    return () => clearTimeout(h);
  }, [clau, ms]);
  return estable;
}

function Bloc({
  n,
  titol,
  extra,
  children,
}: {
  n: number;
  titol: string;
  extra?: ReactNode;
  children: ReactNode;
}) {
  const id = `nova-bloc-${n}`;
  return (
    <section className="panel nova__bloc" aria-labelledby={id}>
      <div className="nova__bloc-cap">
        <span className="nova__num" aria-hidden="true">
          {n}
        </span>
        <h2 id={id}>{titol}</h2>
        {extra ? (
          <>
            <span className="spacer" />
            <span className="dim t-footnote">{extra}</span>
          </>
        ) : null}
      </div>
      {children}
    </section>
  );
}

/**
 * W-05 Nova activació (W-18 quan el destí és de crisi): un sol formulari en tres blocs —
 * què passa, a qui s'avisa, i un resum per revisar i enviar. Es mantenen la validació, els
 * permisos (crítica), la previsualització de destinataris i l'alta idempotent (RF-020…RF-058).
 */
export function W05NovaActivacio() {
  const { t } = useTranslation('operacio');
  const tc = useTranslation('comu').t;
  const navigate = useNavigate();
  const { pot } = useMe();
  const missatgeError = useMissatgeError();
  const [clau] = useState(novaClau);
  const [cerca, setCerca] = useState('');
  const [desti, setDesti] = useState<ServeiCercaDto | null>(null);
  const [critica, setCritica] = useState(false);
  const [titol, setTitol] = useState('');
  const [descripcio, setDescripcio] = useState('');
  const [detalls, setDetalls] = useState(false);
  const [proves, setProves] = useState('');
  const [inici, setInici] = useState('');
  const [zonaId, setZonaId] = useState('');
  const [zonaText, setZonaText] = useState('');
  const [contacte, setContacte] = useState('');
  const [risc, setRisc] = useState('');
  const [perills, setPerills] = useState('');
  const [mesures, setMesures] = useState('');
  const [missatgeEditat, setMissatgeEditat] = useState<string | null>(null);
  const [duplicada, setDuplicada] = useState<unknown>(null);

  const serveis = useCercaServeis(cerca);
  const zones = useZones();
  const potCritica = pot('cai.activacions.critica', 'ALTA', {
    areaId: desti?.areaId,
    serveiId: desti?.serveiId,
    departamentId: desti?.departamentId,
  });
  const esCrisi = desti?.tipus === 'CRISI';
  const criticaPermesa = potCritica;
  const esCritica = critica && criticaPermesa;
  const iniciIso = inici ? isoAmbZona(inici) : '';
  const tePlantilla = desti?.plantillaId != null;

  const cos: ActivacioAltaDto = {
    serveiIds: desti?.serveiId != null ? [desti.serveiId] : [],
    departamentId: desti?.serveiId == null ? (desti?.departamentId ?? null) : null,
    critica: esCritica,
    titol: titol.trim(),
    descripcio: descripcio.trim() || undefined,
    provesRealitzades: proves.trim() || undefined,
    iniciIncident: iniciIso ? new Date(iniciIso).toISOString() : undefined,
    zonaId: zonaId ? Number(zonaId) : null,
    zonaText: zonaText.trim() || undefined,
    contacteText: contacte.trim() || undefined,
    risc: risc.trim() || undefined,
    perills: perills.trim() || undefined,
    mesures: mesures.trim() || undefined,
    plantillaId: desti?.plantillaId ?? null,
    variables: {
      servei: desti?.serveiNom ?? desti?.departamentNom ?? '',
      zona: zonaText || (zones.data?.find((z) => String(z.id) === zonaId)?.nom ?? ''),
      inici: iniciIso,
    },
  };
  // La previsualització (destinataris i missatge) es recalcula quan el formulari s'estabilitza.
  const cosPrevi = useEstable(cos, RETARD_PREVI_MS);
  const previ = useQuery({
    queryKey: ['previsualitzacio', cosPrevi],
    queryFn: () => accionsApi.previsualitzar(cosPrevi),
    enabled: cosPrevi.serveiIds.length > 0 || cosPrevi.departamentId != null,
    retry: false,
  });
  const missatge = missatgeEditat ?? previ.data?.missatge ?? '';
  const totalPersones = BLOCS.reduce((s, b) => s + (previ.data?.destinataris[b]?.length ?? 0), 0);

  const [enviant, setEnviant] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const envia = async (forcar: boolean) => {
    setEnviant(true);
    setError(null);
    try {
      const r = await accionsApi.crear({ ...cos, missatge: missatge || undefined }, clau, forcar);
      navigate(`/activacions/${r.id}`);
    } catch (e) {
      if (e instanceof ApiError && e.status === 409 && e.codi === 'error.activacio.duplicada') {
        setDuplicada(e);
      } else {
        setError(e);
      }
      setEnviant(false);
    }
  };

  // Mateixes condicions que els passos de l'antic assistent, ara totes alhora.
  const falta = [
    desti == null ? t('nova.falta.servei') : null,
    titol.trim().length === 0 ? t('nova.falta.titol') : null,
  ].filter((x): x is string => x != null);

  const blocDestinataris = (bloc: BlocDestinatari) => {
    const persones = previ.data?.destinataris[bloc] ?? [];
    if (persones.length === 0) return null;
    return (
      <div className="list-row" data-bloc={bloc} key={bloc}>
        {persones.length === 1 ? (
          <Avatar nom={persones[0]?.nom} />
        ) : (
          <span className="avatar bg-gray">{persones.length}</span>
        )}
        <div className="list-row__text">
          <div className="list-row__label strong">{tc(`blocDestinatari.${bloc}`)}</div>
          <div className="list-row__sub">
            {persones
              .slice(0, 3)
              .map((p) => p.nom)
              .join(', ')}
            {' · '}
            <span>{t('assistent.persones', { count: persones.length })}</span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <section className="pantalla" data-screen={esCrisi ? 'W-18' : 'W-05'}>
      <Toolbar
        titol={t('titols.novaActivacio')}
        subtitol={t('nova.subtitol')}
        torna={{ ruta: '/activacions', text: t('titols.activacions') }}
      />
      <div className="content">
        <div className="nova">
          <form
            className="nova__form"
            onSubmit={(e) => {
              e.preventDefault();
              if (falta.length === 0 && !enviant) void envia(false);
            }}
            id="form-nova"
            noValidate
          >
            <Bloc n={1} titol={t('nova.blocs.que')}>
              <div className="camp">
                <span className="camp__label" id="nova-servei">
                  {t('nova.servei')}
                </span>
                {desti ? (
                  <div className="panel panel--pla">
                    <div className="opcio" aria-labelledby="nova-servei">
                      <span className={`list-row__icon ${iconaServei(desti)[1]}`}>
                        <Icona nom={iconaServei(desti)[0]} />
                      </span>
                      <span className="list-row__text">
                        <span className="list-row__label strong" style={{ display: 'block' }}>
                          {desti.serveiNom ?? desti.departamentNom}
                        </span>
                        <span className="list-row__sub" style={{ display: 'block' }}>
                          {[desti.areaNom, desti.departamentNom].filter(Boolean).join(' · ')}
                        </span>
                      </span>
                      <BadgeArea tipus={desti.tipus} />
                      <button type="button" className="btn btn--s" onClick={() => setDesti(null)}>
                        {t('nova.canviaServei')}
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <label className="search">
                      <Icona nom="magnifier" />
                      <input
                        type="search"
                        placeholder={t('assistent.cercaServei')}
                        aria-label={t('assistent.cercaServei')}
                        value={cerca}
                        onChange={(e) => setCerca(e.target.value)}
                      />
                    </label>
                    <div className="panel panel--pla">
                      <div className="nova__serveis" role="listbox" aria-labelledby="nova-servei">
                        {(serveis.data ?? []).map((s) => {
                          const [icona, fons] = iconaServei(s);
                          return (
                            <button
                              key={`${s.departamentId}-${s.serveiId ?? 'd'}`}
                              type="button"
                              className="opcio"
                              role="option"
                              aria-selected={false}
                              onClick={() => setDesti(s)}
                            >
                              <span className={`list-row__icon ${fons}`}>
                                <Icona nom={icona} />
                              </span>
                              <span className="list-row__text">
                                <span className="list-row__label" style={{ display: 'block' }}>
                                  {s.serveiNom ?? s.departamentNom}
                                </span>
                                <span className="list-row__sub" style={{ display: 'block' }}>
                                  {[s.areaNom, s.departamentNom].filter(Boolean).join(' · ')}
                                </span>
                              </span>
                              {s.serveiId == null ? (
                                <span className="badge badge--gray">
                                  {t('assistent.departamentSencer')}
                                </span>
                              ) : (
                                <BadgeArea tipus={s.tipus} />
                              )}
                            </button>
                          );
                        })}
                        {serveis.data && serveis.data.length === 0 ? (
                          <Buit icona="magnifier">{t('assistent.senseServeis')}</Buit>
                        ) : null}
                      </div>
                      <div className="list-foot">{t('assistent.serveiText')}</div>
                    </div>
                  </>
                )}
              </div>

              {criticaPermesa ? (
                <label className="nova__critica">
                  <input
                    type="checkbox"
                    checked={esCritica}
                    onChange={(e) => setCritica(e.target.checked)}
                  />
                  {t('nova.marcaCritica')}
                </label>
              ) : desti ? (
                <p className="t-caption dim" style={{ margin: 0 }}>
                  {t('assistent.senseCritica')}
                </p>
              ) : null}

              <Camp etiqueta={t('assistent.camps.titol')}>
                <input
                  className="input"
                  value={titol}
                  onChange={(e) => setTitol(e.target.value)}
                  required
                  aria-required="true"
                />
              </Camp>
              <Camp etiqueta={t('nova.descripcio')}>
                <textarea
                  className="input"
                  rows={3}
                  value={descripcio}
                  onChange={(e) => setDescripcio(e.target.value)}
                />
              </Camp>
              {detalls ? (
                <div className="stack" id="nova-detalls">
                  <Camp etiqueta={t('assistent.camps.proves')}>
                    <textarea
                      className="input"
                      rows={2}
                      value={proves}
                      onChange={(e) => setProves(e.target.value)}
                    />
                  </Camp>
                  <div className="grid grid-2" style={{ gap: 12 }}>
                    <Camp etiqueta={t('assistent.camps.inici')}>
                      <input
                        className="input"
                        type="datetime-local"
                        value={inici}
                        onChange={(e) => setInici(e.target.value)}
                      />
                    </Camp>
                    <Camp etiqueta={t('assistent.camps.zona')}>
                      <Desplegable
                        aria-label={t('assistent.camps.zona')}
                        valor={zonaId}
                        onCanvia={setZonaId}
                        opcions={[
                          { valor: '', etiqueta: t('assistent.senseZona') },
                          ...(zones.data ?? []).map((z) => ({
                            valor: String(z.id),
                            etiqueta: z.nom,
                          })),
                        ]}
                      />
                    </Camp>
                    <Camp etiqueta={t('assistent.camps.ubicacio')}>
                      <input
                        className="input"
                        value={zonaText}
                        onChange={(e) => setZonaText(e.target.value)}
                      />
                    </Camp>
                    <Camp etiqueta={t('assistent.camps.contacte')}>
                      <input
                        className="input"
                        value={contacte}
                        onChange={(e) => setContacte(e.target.value)}
                      />
                    </Camp>
                  </div>
                  {tePlantilla ? (
                    <>
                      <div className="info-banda">
                        <Icona nom="template" />
                        {t('assistent.plantillaText')}
                      </div>
                      <Camp etiqueta={t('assistent.camps.risc')}>
                        <input
                          className="input"
                          value={risc}
                          onChange={(e) => setRisc(e.target.value)}
                        />
                      </Camp>
                      <Camp etiqueta={t('assistent.camps.perills')}>
                        <textarea
                          className="input"
                          rows={2}
                          value={perills}
                          onChange={(e) => setPerills(e.target.value)}
                        />
                      </Camp>
                      <Camp etiqueta={t('assistent.camps.mesures')}>
                        <textarea
                          className="input"
                          rows={2}
                          value={mesures}
                          onChange={(e) => setMesures(e.target.value)}
                        />
                      </Camp>
                    </>
                  ) : desti ? (
                    <div className="t-caption dim">{t('assistent.sensePlantillaText')}</div>
                  ) : null}
                </div>
              ) : null}
              <button
                type="button"
                className="btn"
                style={{ alignSelf: 'flex-start' }}
                aria-expanded={detalls}
                aria-controls="nova-detalls"
                onClick={() => setDetalls((v) => !v)}
              >
                <Icona nom={detalls ? 'minus' : 'plus'} />
                {detalls
                  ? t('nova.amagaDetalls')
                  : tePlantilla
                    ? t('nova.detallsPlantilla')
                    : t('nova.detalls')}
              </button>
            </Bloc>

            <Bloc
              n={2}
              titol={t('nova.blocs.qui')}
              extra={
                desti
                  ? t('nova.proposat', { servei: desti.serveiNom ?? desti.departamentNom })
                  : null
              }
            >
              {desti ? (
                <>
                  <div className="info-banda">
                    <Icona nom="info-circle" />
                    {t('nova.circuitText')}
                  </div>
                  {totalPersones > 0 ? (
                    <div className="panel panel--pla">{BLOCS.map((b) => blocDestinataris(b))}</div>
                  ) : null}
                </>
              ) : (
                <p className="dim t-footnote" style={{ margin: 0 }}>
                  {t('nova.triaServeiPrimer')}
                </p>
              )}
              {desti ? (
                <Camp etiqueta={t('assistent.camps.missatge')}>
                  <textarea
                    className="input"
                    rows={5}
                    value={missatge}
                    onChange={(e) => setMissatgeEditat(e.target.value)}
                  />
                </Camp>
              ) : null}
            </Bloc>
          </form>

          <aside className="panel nova__resum" aria-labelledby="nova-bloc-3">
            <div className="nova__bloc-cap" style={{ marginBottom: 10 }}>
              <span className="nova__num" aria-hidden="true">
                3
              </span>
              <h2 id="nova-bloc-3">{t('nova.blocs.revisa')}</h2>
            </div>
            <div className="hstack gap-8">
              {esCritica ? (
                <span className="badge badge--critic">{tc('tipusActivacio.CRITICA')}</span>
              ) : null}
              <BadgeArea tipus={desti?.tipus} />
            </div>
            <strong className="t-headline" style={{ margin: '8px 0' }}>
              {titol.trim() || <span className="dim">{t('nova.senseTitol')}</span>}
            </strong>
            <div className="kv">
              <span className="kv__k">{t('assistent.passos.servei')}</span>
              <span className="kv__v">{desti?.serveiNom ?? desti?.departamentNom ?? '—'}</span>
            </div>
            <div className="kv">
              <span className="kv__k">{t('nova.savisaA')}</span>
              <span className="kv__v">
                {previ.data ? t('assistent.persones', { count: totalPersones }) : '—'}
              </span>
            </div>
            {falta.length > 0 ? (
              <ul className="nova__falta" aria-label={t('nova.falta.etiqueta')}>
                {falta.map((x) => (
                  <li key={x}>{x}</li>
                ))}
              </ul>
            ) : null}
            {esCritica ? (
              <div className="avis-banda" style={{ marginTop: 12 }}>
                <Icona nom="exclamationmark-triangle" />
                {t('assistent.avisCritica')}
              </div>
            ) : null}
            {duplicada ? (
              <div className="avis-banda" role="alert" style={{ marginTop: 12 }}>
                <Icona nom="exclamationmark-triangle" />
                <span className="spacer">{missatgeError(duplicada)}</span>
                <button
                  type="button"
                  className="btn btn--xs"
                  disabled={enviant}
                  onClick={() => void envia(true)}
                >
                  {t('assistent.enviaIgualment')}
                </button>
              </div>
            ) : null}
            {error ? (
              <p className="error-text" role="alert">
                {missatgeError(error)}
              </p>
            ) : null}
            <div className="stack stack--s" style={{ marginTop: 16 }}>
              <button
                type="submit"
                form="form-nova"
                className={`btn btn--l ${esCritica ? 'btn--critic' : 'btn--primary'}`}
                disabled={enviant || falta.length > 0}
              >
                <Icona nom="paperplane" />
                {esCritica ? t('assistent.enviaCritica') : t('assistent.envia')}
              </button>
              <Link className="btn" to="/activacions">
                {tc('accions.cancellar')}
              </Link>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}
